import { ApiRequestError, buildApiRequestError } from './errors'

export type AuthTokens = {
  accessToken: string
  refreshToken: string
  expTime: number
}

export type UserNotification = {
  id: number
  type: 'INFO' | 'SUCCESS' | 'WARNING' | string
  title: string
  message: string
  actionUrl?: string | null
  read: boolean
  createdAt: string
}

export type UserNotificationsPage = {
  items: UserNotification[]
  total: number
  unreadCount: number
  limit: number
  offset: number
}

export type UserTransactionType = 'SPEND' | 'REFUND' | 'PURCHASE' | 'BONUS' | 'ADJUST' | string

export type UserTransactionItem = {
  id: number
  type: UserTransactionType
  amountCredits: number
  balanceBefore: number
  balanceAfter: number
  description: string
  jobId?: number | null
  createdAt: string
}

export type UserTransactionsPage = {
  items: UserTransactionItem[]
  total: number
  nextOffset: number
  hasMore: boolean
}

export type TelegramStatus = {
  enabled: boolean
  bindAvailable: boolean
  botId?: number | null
  linked: boolean
  telegramUserId?: string | null
  username?: string | null
  displayName?: string | null
  avatarUrl?: string | null
  linkedAt?: string | null
  channelUsername?: string | null
  channelUrl?: string | null
  rewardCredits: number
  subscriptionStatus?: string | null
  subscribed: boolean
  lastCheckedAt?: string | null
  verifiedAt?: string | null
  rewardGranted: boolean
  rewardGrantedAt?: string | null
  rewardGrantedCredits: number
  canCheckSubscription: boolean
}

export type RatingRewardStatus = {
  enabled: boolean
  credits: number
  dailyLimit: number
  grantedToday: number
  remainingToday: number
  dailyLimitReached: boolean
}

export type JobMetadataDraft = {
  productName?: string | null
  productCategory?: string | null
  productCategoryOther?: string | null
}

export type SeriesClarificationQuestion = {
  id: string
  label: string
  hint?: string | null
  type: 'text' | 'number' | 'single_choice'
  unit?: string | null
  options: string[]
  allowCustom: boolean
}

export type SeriesClarificationsResponse = {
  version: number
  questions: SeriesClarificationQuestion[]
}

export type ProjectListItem = {
  id: number
  name: string
  pipelineId?: string | null
  category?: string | null
  categoryLabel?: string | null
  folderId?: number | null
  folderName?: string | null
  tags?: string[]
  latestOutputUrl?: string | null
  latestOutputAspect?: number | null
  imagesCount?: number
  videosCount?: number
  createdAt: string
  updatedAt: string
}

export type ProjectFolderItem = {
  id: number
  name: string
  count: number
  createdAt: string
  updatedAt: string
}

export type ProjectCategoryItem = {
  code: string
  label: string
  count: number
}

export type ProjectRecord = {
  id: number
  name: string
  pipelineId?: string | null
  snapshot: Record<string, any>
  board: Record<string, any>
  boardGroupsOffset?: number | null
  boardGroupsLimit?: number | null
  boardGroupsTotal?: number | null
  boardGroupsHasMore?: boolean | null
  boardGroupsNextOffset?: number | null
  createdAt: string
  updatedAt: string
}

export type ProjectRunGroupRecord = {
  id: number
  groupNumber: number
  createdAt: string
  pipelineId: string
  pipelineTitle?: string | null
  status: 'starting' | 'running' | 'succeeded' | 'failed' | string
  progress: number
  snapshot: Record<string, any>
  error?: string | null
  jobId?: number | null
  batchId?: string | null
  seriesV2State?: {
    hasMainCard: boolean
    mainCardSlideId?: number | null
    mainCardReady: boolean
  } | null
}

export type ProjectBoardCardRecord = {
  id: number
  runGroupId: number
  groupNumber: number
  lineageId: number
  createdAt: string
  assetIndex: number
  status: 'starting' | 'running' | 'succeeded' | 'failed' | string
  aspect: number
  output?: {
    id: number
    url: string
    mime?: string | null
    type?: string | null
    jobId?: number | null
  } | null
  error?: string | null
}

export type ProjectsPage = {
  items: ProjectListItem[]
  total: number
  nextOffset: number
  hasMore: boolean
}

export const API_BASE = import.meta.env.VITE_API_BASE_URL || (typeof window !== 'undefined' ? `${window.location.origin}` : '')

function isIpBannedResponse(status: number, headers: Headers, bodyText: string): boolean {
  if (status !== 403) return false
  if (headers.get('X-IP-Banned')) return true
  return String(bodyText || '').trim().toLowerCase() === 'your ip is banned'
}

function createIpBannedError(): ApiRequestError {
  return new ApiRequestError({
    status: 403,
    code: 'ip_banned',
    userMessage: 'Your IP is banned',
    rawMessage: 'Your IP is banned'
  })
}

function getAuthHeader(token?: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {}
}

class AuthClient {
  private accessToken: string | null = null
  private refreshToken: string | null = null
  private expTime: number | null = null
  private refreshing: Promise<boolean> | null = null
  private lastRefreshUnauthorized = false
  private lastRefreshIpBanned = false

  setTokens(tokens: AuthTokens | null) {
    this.accessToken = tokens?.accessToken ?? null
    this.refreshToken = tokens?.refreshToken ?? null
    if (tokens?.expTime != null) {
      const exp = Number(tokens.expTime)
      if (Number.isFinite(exp)) {
        // Backend returns epoch seconds; normalize to milliseconds for comparisons
        this.expTime = exp > 1e12 ? exp : exp * 1000
      } else {
        this.expTime = null
      }
    } else {
      this.expTime = null
    }
  }

  get tokens() {
    return { accessToken: this.accessToken, refreshToken: this.refreshToken }
  }

  didLastRefreshFailUnauthorized(): boolean {
    return this.lastRefreshUnauthorized
  }

  didLastRefreshFailIpBanned(): boolean {
    return this.lastRefreshIpBanned
  }

  isExpiringSoon(thresholdMs = 5 * 60 * 1000): boolean {
    if (!this.expTime) return false
    return (this.expTime - Date.now()) < thresholdMs
  }

  isExpired(): boolean {
    if (!this.expTime) return false
    return Date.now() >= this.expTime
  }

  async refreshOnce(): Promise<boolean> {
    if (this.refreshing) return this.refreshing
    this.lastRefreshIpBanned = false
    if (!this.refreshToken) {
      this.lastRefreshUnauthorized = true
      return false
    }
    this.refreshing = (async () => {
      this.lastRefreshUnauthorized = false
      this.lastRefreshIpBanned = false
      try {
        // Refresh via GET with Authorization: Bearer <refreshToken>
        const res = await fetch(`${API_BASE}/v1/users/auth-token-refresh`, {
          method: 'GET',
          headers: getAuthHeader(this.refreshToken)
        })
        if (res.status === 401) {
          this.lastRefreshUnauthorized = true
          return false
        }
        if (!res.ok) {
          const txt = await res.text().catch(() => '')
          this.lastRefreshIpBanned = isIpBannedResponse(res.status, res.headers, txt)
          return false
        }
        const data: AuthTokens = await res.json()
        this.setTokens(data)
        window.localStorage.setItem('auth.tokens', JSON.stringify(data))
        this.lastRefreshUnauthorized = false
        this.lastRefreshIpBanned = false
        return true
      } catch {
        return false
      } finally {
        this.refreshing = null
      }
    })()
    return this.refreshing
  }

  getAuthorizationHeader(): Record<string, string> {
    return getAuthHeader(this.accessToken)
  }
}

export const auth = new AuthClient()

export class ApiClient {
  // thin wrapper using AuthClient
  private onRefreshed: (() => void) | null = null
  private onExpired: (() => void) | null = null
  private onBanned: (() => void) | null = null

  onAuthRefreshed(cb: (() => void) | null) {
    this.onRefreshed = cb
  }

  onAuthBanned(cb: (() => void) | null) {
    this.onBanned = cb
  }

  // Billing: credit packs
  async listCreditPacks(currency: string = 'RUB'): Promise<Array<{ code: string; title: string; credits: number; priceMinor: number; oldPriceMinor?: number | null; currency: string; paymentDisabledReason?: string | null; foreignCardPaymentDisabledReason?: string | null }>> {
    const qs = new URLSearchParams({ currency }).toString()
    return this.request(`/api/billing/packs?${qs}`)
  }

  async checkoutCreditPack(
    packCode: string,
    currency: string = 'RUB',
    returnUrl?: string,
    promoCode?: string,
    provider?: 'yookassa' | 'robokassa',
    receiptEmail?: string,
    attribution?: { yandexClientId?: string; yandexClickId?: string }
  ): Promise<{ paymentId: number; providerPaymentId: string; confirmationUrl: string }> {
    const body: any = { packCode, currency }
    if (returnUrl) body.returnUrl = returnUrl
    if (promoCode) body.promoCode = promoCode
    if (provider) body.provider = provider
    if (receiptEmail) body.receiptEmail = receiptEmail
    if (attribution?.yandexClientId) body.yandexClientId = attribution.yandexClientId
    if (attribution?.yandexClickId) body.yandexClickId = attribution.yandexClickId
    return this.request(`/api/billing/checkout`, { method: 'POST', body: JSON.stringify(body) })
  }

  async getBillingPaymentStatus(paymentId: number): Promise<{
    paymentId: number
    providerPaymentId: string
    status: 'INITIATED' | 'PENDING' | 'PAID' | 'CANCELED' | 'FAILED' | string
    amountMinor: number
    currency: string
    credits: number
    paidAt?: string | null
  }> {
    return this.request(`/api/billing/payments/${paymentId}`)
  }

  async listPendingBillingPayments(limit = 20): Promise<{
    items: Array<{
      paymentId: number
      providerPaymentId: string
      status: 'INITIATED' | 'PENDING' | 'PAID' | 'CANCELED' | 'FAILED' | string
      amountMinor: number
      currency: string
      credits: number
      paidAt?: string | null
    }>
  }> {
    const qs = new URLSearchParams({ limit: String(limit) }).toString()
    return this.request(`/api/billing/payments/pending?${qs}`)
  }

  async validatePromoCode(code: string): Promise<{ valid: boolean; discountPercent: number; codeId: number | null; partnerCode?: boolean; reason?: string | null; alreadyUsed?: boolean; firstTopupRequired?: boolean; partnerConflict?: boolean }> {
    const qs = new URLSearchParams({ code }).toString()
    return this.request(`/v1/promo/validate?${qs}`)
  }

  onAuthExpired(cb: (() => void) | null) {
    this.onExpired = cb
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const hasSession = () => {
      const tokens = auth.tokens
      return Boolean(tokens.accessToken || tokens.refreshToken)
    }

    const doFetch = async () => {
      const target = (path.startsWith('http://') || path.startsWith('https://'))
        ? path
        : `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`
      const res = await fetch(target, {
        ...init,
        headers: {
          'Content-Type': 'application/json',
          ...(init.headers || {}),
          ...auth.getAuthorizationHeader()
        }
      })
      return res
    }

    const triggerExpired = () => {
      this.onExpired?.()
    }

    const ensureSessionFresh = async (forceRefresh: boolean): Promise<'ok' | 'no-session' | 'expired' | 'banned'> => {
      if (!hasSession()) return 'no-session'

      const needsRefresh = forceRefresh || auth.isExpired() || auth.isExpiringSoon(5 * 60 * 1000)
      if (!needsRefresh) return 'ok'

      try {
        const refreshed = await auth.refreshOnce()
        if (refreshed) {
          this.onRefreshed?.()
          return 'ok'
        }
      } catch {
        // swallow and treat as failure below
      }

      if (auth.didLastRefreshFailIpBanned()) return 'banned'
      return 'expired'
    }

    const sessionState = await ensureSessionFresh(false)
    if (sessionState === 'banned') {
      this.onBanned?.()
      throw createIpBannedError()
    }
    if (sessionState === 'expired') {
      triggerExpired()
      throw new ApiRequestError({
        status: 401,
        code: 'session_expired',
        userMessage: 'Сессия истекла. Войдите снова.',
        rawMessage: 'Session expired'
      })
    }

    let res = await doFetch()
    if (res.status === 401) {
      const refreshState = await ensureSessionFresh(true)
      if (refreshState === 'banned') {
        this.onBanned?.()
        throw createIpBannedError()
      }
      if (refreshState === 'expired') {
        triggerExpired()
        throw new ApiRequestError({
          status: 401,
          code: 'session_expired',
          userMessage: 'Сессия истекла. Войдите снова.',
          rawMessage: 'Session expired'
        })
      }

      if (refreshState === 'ok') {
        res = await doFetch()
        if (res.status === 401) {
          triggerExpired()
          throw new ApiRequestError({
            status: 401,
            code: 'session_expired',
            userMessage: 'Сессия истекла. Войдите снова.',
            rawMessage: 'Session expired'
          })
        }
      }
    }
    if (!res.ok) {
      const txt = await res.text().catch(() => '')
      if (isIpBannedResponse(res.status, res.headers, txt)) {
        this.onBanned?.()
      }
      throw buildApiRequestError({
        status: res.status,
        bodyText: txt,
        requestId: res.headers.get('X-Request-Id') || undefined
      })
    }
    const ct = res.headers.get('content-type') || ''
    if (ct.includes('application/json')) return res.json()
    // @ts-ignore
    return res.text()
  }

  // Auth
  async authGoogle(
    idToken: string,
    opts?: { partnerCode?: string; partnerId?: number; referralCode?: string; referrerId?: number }
  ): Promise<AuthTokens> {
    const body: any = { idToken }
    if (opts?.referralCode) body.referralCode = opts.referralCode
    if (opts?.referrerId) body.referrerId = opts.referrerId
    if (opts?.partnerCode) body.partnerCode = opts.partnerCode
    if (opts?.partnerId) body.partnerId = opts.partnerId
    const data = await this.request<AuthTokens>(`/v1/auth/google`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
    auth.setTokens(data)
    window.localStorage.setItem('auth.tokens', JSON.stringify(data))
    return data
  }

  async authTelegram(payload: Record<string, string | number>): Promise<AuthTokens> {
    const data = await this.request<AuthTokens>(`/v1/auth/telegram`, {
      method: 'POST',
      body: JSON.stringify(payload)
    })
    auth.setTokens(data)
    window.localStorage.setItem('auth.tokens', JSON.stringify(data))
    return data
  }

  async getTelegramStatus(): Promise<TelegramStatus> {
    return this.request(`/v1/users/telegram`)
  }

  async getRatingRewardStatus(): Promise<RatingRewardStatus> {
    return this.request(`/v1/users/rating-reward`)
  }

  async bindTelegram(payload: Record<string, string | number>): Promise<TelegramStatus> {
    return this.request(`/v1/users/telegram/bind`, {
      method: 'POST',
      body: JSON.stringify(payload)
    })
  }

  async verifyTelegramSubscription(): Promise<TelegramStatus> {
    return this.request(`/v1/users/telegram/verify-subscription`, {
      method: 'POST'
    })
  }

  // Pipelines
  listPipelines(): Promise<Array<{
    id: string
    title: string
    description?: string | null
    order: number
    pipelines: Array<{ id: string; title: string; shortDesc?: string | null; badge?: string | null; basePrice: number; previewInputUrl?: string | null; previewResultUrl?: string | null; categoryId?: string | null; enabled?: boolean; reasonDisabled?: string | null }>
  }>> {
    return this.request(`/api/pipelines`)
  }
  getPipeline(id: string): Promise<{ id: string; title: string; shortDesc?: string | null; badge?: string | null; basePrice: number; previewInputUrl?: string | null; previewResultUrl?: string | null; categoryId?: string | null; enabled?: boolean; reasonDisabled?: string | null }> {
    return this.request(`/api/pipelines/${encodeURIComponent(id)}`)
  }
  getPipelineSchema(id: string): Promise<{
    version: number
    pipelineVersion?: number
    pipelineVersionId?: number
    overrideEnabled?: boolean
    ui: { title: string; description?: string | null }
    inputs: Array<{
      key?: string
      multiple?: boolean
      minFiles?: number
      maxFiles?: number
      allowedMime?: string[]
      isMetadataSource?: boolean
      title?: string | null
      description?: string | null
      uiOrder?: number | null
      requestOrder?: number | null
    }>
    groups?: Array<{
      id: string
      title: string
      order: number
      color?: string | null
    }>
    params: Record<string, {
      orderId: number
      type: string
      title?: string | null
      uiType?: string | null
      values?: any
      default?: any
      minimum?: number | null
      maximum?: number | null
      minLength?: number | null
      maxLength?: number | null
      minItems?: number | null
      maxItems?: number | null
      linkedKey?: string | null
      children?: Array<{ key: string; def: any }> | null
      multiline?: boolean
      tooltip?: string | null
      hint?: string | null
      help?: string | null
      ideaEnabled?: boolean
      additionally?: boolean
      enabled?: boolean
      group?: string | null
    }>
  }> {
    return this.request<any>(`/api/pipelines/${encodeURIComponent(id)}/schema`).then((raw) => {
      try {
        if (raw && raw.params && !Array.isArray(raw.params)) return raw
        if (raw && Array.isArray(raw.params)) {
          const mapDef = (d: any) => {
            const mapped: any = {
              orderId: (typeof d.order === 'number') ? d.order : (typeof d.orderId === 'number' ? d.orderId : 0),
              type: String(d.type || ''),
              title: d.title ?? null,
              uiType: d.uiType ?? null,
              values: d.values ?? null,
              default: d.default ?? null,
              minimum: (typeof d.minimum === 'number') ? d.minimum : null,
              maximum: (typeof d.maximum === 'number') ? d.maximum : null,
              minLength: (typeof d.minLength === 'number') ? d.minLength : null,
              maxLength: (typeof d.maxLength === 'number') ? d.maxLength : null,
              minItems: (typeof d.minItems === 'number') ? d.minItems : null,
              maxItems: (typeof d.maxItems === 'number') ? d.maxItems : null,
              linkedKey: (typeof d.linkedKey === 'string') ? d.linkedKey : null,
              multiline: d.multiline === true,
              tooltip: d.tooltip ?? null,
              hint: d.hint ?? null,
              help: d.help ?? null,
              ideaEnabled: d.ideaEnabled === true,
              additionally: d.additionally === true,
              enabled: d.enabled !== false,
              group: d.group ?? null
            }
            if (Array.isArray(d.children)) {
              const children = d.children.map((child: any) => {
                const ck = child?.key || child?.name
                const cd = child?.def || child?.value || {}
                if (!ck) return null
                return { key: String(ck), def: mapDef(cd) }
              }).filter(Boolean)
              mapped.children = children.length ? children : null
            }
            return mapped
          }
          const map: Record<string, any> = {}
          for (const item of raw.params) {
            if (!item) continue
            const k = item.key || item.name
            const d = item.def || item.value || {}
            if (!k) continue
            map[k] = mapDef(d)
          }
          return { ...raw, params: map }
        }
      } catch { }
      return raw as any
    })
  }

  // Assets: presign + confirm
  presign(files: Array<{ filename: string; mime: string; sizeBytes: number }>): Promise<{ presigneds: Array<{ uploadToken: string; s3Key: string; url: string; headers: Record<string, string> }> }> {
    return this.request(`/api/assets/presign`, { method: 'POST', body: JSON.stringify({ files, type: 'input' }) })
  }

  async uploadToPresigned(url: string, headersOrFields: Record<string, string>, data: Blob): Promise<void> {
    const isPost = 'policy' in headersOrFields || 'Policy' in headersOrFields || 'x-amz-algorithm' in headersOrFields;

    if (!isPost) {
      const res = await fetch(url, { method: 'PUT', headers: headersOrFields, body: data });
      if (!res.ok) {
        throw new ApiRequestError({
          status: res.status,
          code: `upload_put_failed_${res.status}`,
          userMessage: 'Не удалось загрузить файл на сервер. Попробуйте снова.',
          rawMessage: `Upload (PUT) failed: ${res.status}`
        })
      }
      return;
    }

    const fd = new FormData();
    for (const [k, v] of Object.entries(headersOrFields)) {
      fd.append(k, v);
    }
    fd.append('file', data);

    const res = await fetch(url, {
      method: 'POST',
      body: fd
    });

    if (!(res.status === 204 || res.status === 201 || res.ok)) {
      const text = await res.text().catch(() => '');
      throw new ApiRequestError({
        status: res.status,
        code: `upload_post_failed_${res.status}`,
        userMessage: 'Не удалось загрузить файл на сервер. Попробуйте снова.',
        rawMessage: `Upload (POST) failed: ${res.status} ${text}`
      })
    }
  }

  confirm(files: Array<{ uploadToken: string; s3Key: string; mime: string; sizeBytes: number; width?: number; height?: number }>): Promise<{ assets: Array<{ id: number; s3Key: string; type: string; ttlAt: string }> }> {
    return this.request(`/api/assets/confirm`, { method: 'POST', body: JSON.stringify({ confirmFiles: files }) })
  }

  getAssetPreview(id: number): Promise<{ id: number; url: string; mime?: string | null; type: string }> {
    return this.request(`/api/assets/${id}`)
  }

  async deleteAsset(id: number): Promise<void> {
    await this.request(`/api/assets/${id}`, { method: 'DELETE' })
  }

  // Jobs
  precheckJob(
    pipelineId: string,
    batchId?: string,
    pipelineVersion?: number
  ): Promise<{ ok: boolean; resolvedPipelineVersion: number }> {
    return this.request(`/api/jobs/precheck`, {
      method: 'POST',
      body: JSON.stringify({ pipelineId, batchId, pipelineVersion })
    })
  }

  initJob(
    pipelineId: string,
    batchId?: string,
    pipelineVersion?: number,
    context?: { projectId?: number | null; runGroupId?: string | null; runGroupFk?: number | null }
  ): Promise<{ jobId: number; status: string }> {
    return this.request(`/api/jobs/init`, {
      method: 'POST',
      body: JSON.stringify({
        pipelineId,
        batchId,
        pipelineVersion,
        projectId: context?.projectId ?? null,
        runGroupId: context?.runGroupId ?? null,
        runGroupFk: context?.runGroupFk ?? null
      })
    })
  }
  configureJob(
    id: number,
    body: {
      inputAssetIds?: number[]
      inputs?: Array<{ assetId: number; slot: string; order?: number }>
      params: Record<string, unknown>
      metadata?: JobMetadataDraft
    }
  ): Promise<{ jobId: number; status: string; inputs: number }> {
    return this.request(`/api/jobs/${id}/configure`, { method: 'POST', body: JSON.stringify(body) })
  }
  startJob(id: number): Promise<{ jobId: number; status: string; priceCredits: number }> {
    return this.request(`/api/jobs/${id}/start`, { method: 'POST' })
  }
  async getJob(id: number): Promise<{
    id: number
    projectId?: number | null
    runGroupId?: string | null
    batchId?: string | null
    productName?: string | null
    productCategory?: string | null
    productCategoryOther?: string | null
    status: string
    progress: number
    createdAt: string
    startedAt?: string | null
    finishedAt?: string | null
    error?: string | null
    priceCredits?: number
    thumbUrl?: string | null
    ratingRewardEnabled?: boolean
    ratingRewardCredits?: number
    ratingRewardDailyLimit?: number
    ratingRewardGrantedToday?: number
    ratingRewardRemainingToday?: number
    ratingRewardDailyLimitReached?: boolean
    outputs: Array<{ id: number; url: string; mime?: string | null; type?: string | null; rating: number | null; reasons: string[]; comment?: string | null }>
    urls: string[]
  }> {
    const raw: any = await this.request(`/api/jobs/${id}`)
    const status = String(raw?.status ?? '')
    const progress = Number(raw?.progress ?? 0)
    const createdAt = typeof raw?.createdAt === 'string' ? raw.createdAt : new Date().toISOString()
    const startedAt = raw?.startedAt ? String(raw.startedAt) : null
    const finishedAt = raw?.finishedAt ? String(raw.finishedAt) : null
    const error = raw?.error != null ? String(raw.error) : null
    const priceCredits = raw?.priceCredits != null ? Number(raw.priceCredits) : undefined
    const thumbUrlRaw = raw?.thumbUrl ?? raw?.thumbnailUrl ?? null
    const ratingRewardEnabled = raw?.ratingRewardEnabled === true
    const ratingRewardCreditsRaw = Number(raw?.ratingRewardCredits ?? 0)
    const ratingRewardCredits = Number.isFinite(ratingRewardCreditsRaw) ? ratingRewardCreditsRaw : 0
    const ratingRewardDailyLimitRaw = Number(raw?.ratingRewardDailyLimit ?? 0)
    const ratingRewardGrantedTodayRaw = Number(raw?.ratingRewardGrantedToday ?? 0)
    const ratingRewardRemainingTodayRaw = Number(raw?.ratingRewardRemainingToday ?? 0)
    const ratingRewardDailyLimit = Number.isFinite(ratingRewardDailyLimitRaw) ? Math.max(0, Math.trunc(ratingRewardDailyLimitRaw)) : 0
    const ratingRewardGrantedToday = Number.isFinite(ratingRewardGrantedTodayRaw) ? Math.max(0, Math.trunc(ratingRewardGrantedTodayRaw)) : 0
    const ratingRewardRemainingToday = Number.isFinite(ratingRewardRemainingTodayRaw) ? Math.max(0, Math.trunc(ratingRewardRemainingTodayRaw)) : 0
    const ratingRewardDailyLimitReached = raw?.ratingRewardDailyLimitReached === true

    const batchId = raw?.batchId != null ? String(raw.batchId) : null
    const projectIdRaw = Number(raw?.projectId)
    const projectId = Number.isFinite(projectIdRaw) && projectIdRaw > 0 ? Math.trunc(projectIdRaw) : null
    const runGroupId = raw?.runGroupId != null ? String(raw.runGroupId).trim() : null
    const outputsRaw: any[] = Array.isArray(raw?.outputs) ? raw.outputs : []
    let outputs = outputsRaw
      .map((o: any) => {
        const outId = Number(o?.id)
        const url = typeof o?.url === 'string' ? o.url : ''
        if (!Number.isFinite(outId) || !url) return null
        const ratingValue = o?.rating != null ? Number(o.rating) : null
        const reasonsRaw = Array.isArray(o?.reasons) ? o.reasons : []
        const reasons = reasonsRaw
          .map((item: any) => (typeof item === 'string' ? item : '').trim())
          .filter((item: string) => item.length > 0)
        return {
          id: outId,
          url,
          mime: o?.mime != null ? String(o.mime) : null,
          type: o?.type != null ? String(o.type) : null,
          rating: Number.isFinite(ratingValue) ? ratingValue : null,
          reasons,
          comment: typeof o?.comment === 'string' ? o.comment : null
        }
      })
      .filter(Boolean) as Array<{ id: number; url: string; mime?: string | null; type?: string | null; rating: number | null; reasons: string[]; comment?: string | null }>

    const urlsRaw = Array.isArray(raw?.urls) ? raw.urls.filter((u: any) => typeof u === 'string' && u.length > 0) : []
    if (outputs.length === 0 && urlsRaw.length > 0) {
      outputs = urlsRaw.map((url, idx) => ({
        id: -(idx + 1),
        url,
        mime: /\.(mp4|m4v|mov|webm|avi|mkv)(?:$|[?#])/i.test(url) ? 'video/mp4' : null,
        type: null,
        rating: null,
        reasons: [],
        comment: null
      }))
    }
    const urls = outputs.length ? outputs.map((o) => o.url) : urlsRaw
    const thumbUrl = typeof thumbUrlRaw === 'string' && thumbUrlRaw.length ? thumbUrlRaw : outputs[0]?.url ?? null

    return {
      id: Number(raw?.id ?? id),
      projectId,
      runGroupId,
      batchId,
      productName: raw?.productName != null ? String(raw.productName) : null,
      productCategory: raw?.productCategory != null ? String(raw.productCategory) : null,
      productCategoryOther: raw?.productCategoryOther != null ? String(raw.productCategoryOther) : null,
      status,
      progress,
      createdAt,
      startedAt,
      finishedAt,
      error,
      priceCredits,
      thumbUrl,
      ratingRewardEnabled,
      ratingRewardCredits,
      ratingRewardDailyLimit,
      ratingRewardGrantedToday,
      ratingRewardRemainingToday,
      ratingRewardDailyLimitReached,
      outputs,
      urls
    }
  }

  generatePipelineIdea(
    pipelineId: string,
    body: {
      paramKey: string
      params: Record<string, unknown>
      images?: Array<{ mimeType: string; base64: string }>
      inputAssetIds?: number[]
    }
  ): Promise<{ idea: string }> {
    return this.request(
      `/api/pipelines/${encodeURIComponent(pipelineId)}/idea`,
      { method: 'POST', body: JSON.stringify(body) }
    )
  }

  generatePipelineIdeaSmartStart(
    pipelineId: string,
    body: {
      paramKey: string
      params: Record<string, unknown>
      images?: Array<{ mimeType: string; base64: string }>
      inputAssetIds?: number[]
    }
  ): Promise<{
    quiz: {
      questions: Array<{
        id: string
        title: string
        customAllowed: boolean
        options: Array<{ id: string; title: string }>
      }>
    }
  }> {
    return this.request(
      `/api/pipelines/${encodeURIComponent(pipelineId)}/idea/smart/start`,
      { method: 'POST', body: JSON.stringify(body) }
    )
  }

  generatePipelineIdeaSmartComplete(
    pipelineId: string,
    body: {
      paramKey: string
      params: Record<string, unknown>
      images?: Array<{ mimeType: string; base64: string }>
      inputAssetIds?: number[]
      quiz: {
        questions: Array<{
          id: string
          title: string
          customAllowed: boolean
          options: Array<{ id: string; title: string }>
        }>
      }
      answers: Array<{
        questionId: string
        optionId?: string | null
        customText?: string | null
      }>
    }
  ): Promise<{ idea: string }> {
    return this.request(
      `/api/pipelines/${encodeURIComponent(pipelineId)}/idea/smart/complete`,
      { method: 'POST', body: JSON.stringify(body) }
    )
  }

  generatePipelineAutofill(
    pipelineId: string,
    body: {
      inputAssetIds?: number[]
      inputs?: Array<{ assetId: number; slot: string; order?: number }>
      images?: Array<{ mimeType: string; base64: string }>
      params?: Record<string, unknown>
    }
  ): Promise<{
    productName?: string | null
    productCategory?: string | null
    productCategoryOther?: string | null
    titles?: string[] | string | null
    blocks?: string[] | string | null
  }> {
    return this.request(
      `/api/pipelines/${encodeURIComponent(pipelineId)}/autofill`,
      { method: 'POST', body: JSON.stringify(body) }
    )
  }

  rateJobAsset(jobId: number, assetId: number, rating: number, reasons: string[] = [], comment?: string | null): Promise<{
    jobId: number
    assetId: number
    rating: number
    reasons: string[]
    comment?: string | null
    rewardEnabled?: boolean
    rewardCredits?: number
    rewardGrantedCredits?: number
    rewardDailyLimit?: number
    rewardGrantedToday?: number
    rewardRemainingToday?: number
    rewardDailyLimitReached?: boolean
    refundGranted?: boolean
    refundCredits?: number
    refundReasonCode?: string | null
  }> {
    const body: Record<string, unknown> = { rating, reasons }
    if (comment != null) body.comment = comment
    return this.request(`/api/jobs/${jobId}/assets/${assetId}/rating`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  }

  async listJobAssets(id: number, type: 'OUTPUT' | 'UPSCALED' = 'OUTPUT'): Promise<Array<{ id: number; type: string; mime?: string | null; sizeBytes?: number | null; s3Key: string; url: string; createdAt: string; ttlAt?: string | null; rating: number | null; reasons: string[]; comment?: string | null }>> {
    const raw = await this.request(`/api/jobs/${id}/assets?type=${type}`)
    if (!Array.isArray(raw)) return []
    return raw
      .map((item: any) => {
        const assetId = Number(item?.id)
        const url = typeof item?.url === 'string' ? item.url : ''
        if (!Number.isFinite(assetId) || !url) return null
        const ratingValue = item?.rating != null ? Number(item.rating) : null
        const reasonsRaw = Array.isArray(item?.reasons) ? item.reasons : []
        const reasons = reasonsRaw
          .map((entry: any) => (typeof entry === 'string' ? entry : '').trim())
          .filter((entry: string) => entry.length > 0)
        return {
          id: assetId,
          type: String(item?.type ?? ''),
          mime: item?.mime ?? null,
          sizeBytes: item?.sizeBytes != null ? Number(item.sizeBytes) : null,
          s3Key: String(item?.s3Key ?? ''),
          url,
          createdAt: String(item?.createdAt ?? ''),
          ttlAt: item?.ttlAt != null ? String(item.ttlAt) : null,
          rating: Number.isFinite(ratingValue) ? ratingValue : null,
          reasons,
          comment: typeof item?.comment === 'string' ? item.comment : null
        }
      })
      .filter(Boolean) as Array<{ id: number; type: string; mime?: string | null; sizeBytes?: number | null; s3Key: string; url: string; createdAt: string; ttlAt?: string | null; rating: number | null; reasons: string[]; comment?: string | null }>
  }

  // Upscale: backend currently registers as GET but expects a body; browsers cannot send a body with GET.
  // Implement as POST per spec. Backend should accept JSON body { outputAssetId, scale }
  async upscaleJob(parentJobId: number, outputAssetId: number, scale: '2' | '4' = '2'): Promise<{ upscaleJobId: number; priceCredits: number }> {
    return this.request(`/api/jobs/${parentJobId}/upscale`, {
      method: 'POST',
      body: JSON.stringify({ outputAssetId, scale })
    })
  }

  async getMe(): Promise<{
    id: number
    email: string
    role?: string
    balance?: number
    reservedCredits?: number
    concurrentLaunchLimit?: number
    name?: string
    avatarUrl?: string
    preferences?: {
      seriesClarificationsAutoPromptEnabled?: boolean
    }
  }> {
    return this.request(`/v1/users/me`)
  }

  async updateUserPreferences(body: {
    seriesClarificationsAutoPromptEnabled: boolean
  }): Promise<{
    seriesClarificationsAutoPromptEnabled: boolean
  }> {
    return this.request(`/v1/users/me/preferences`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    })
  }

  async analyzeSeriesClarifications(body: {
    assetIds: number[]
    userContext?: string | null
    productName?: string | null
    productCategory?: string | null
  }): Promise<SeriesClarificationsResponse> {
    return this.request(`/api/ai/series-v2/clarifications`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  }

  async listUserTransactions(params: { limit?: number; offset?: number } = {}): Promise<UserTransactionsPage> {
    const qs = new URLSearchParams()
    if (params.limit != null) qs.set('limit', String(params.limit))
    if (params.offset != null) qs.set('offset', String(params.offset))
    const suffix = qs.toString()
    return this.request(`/v1/users/transactions${suffix ? `?${suffix}` : ''}`)
  }

  async getUserSurveyStatus(surveyKey: string): Promise<{
    surveyKey: string
    answered: boolean
    answerKey?: string | null
    answerLabel?: string | null
    answerKeys?: string[]
    answerLabels?: string[] | null
    answeredAt?: string | null
  }> {
    return this.request(`/v1/users/surveys/${encodeURIComponent(surveyKey)}`)
  }

  async submitUserSurveyResponse(
    surveyKey: string,
    body: { answerKey?: string | null; answerLabel?: string | null; answerKeys?: string[]; answerLabels?: string[] | null }
  ): Promise<{
    surveyKey: string
    answered: boolean
    answerKey?: string | null
    answerLabel?: string | null
    answerKeys?: string[]
    answerLabels?: string[] | null
    answeredAt?: string | null
  }> {
    return this.request(`/v1/users/surveys/${encodeURIComponent(surveyKey)}`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  }

  async listJobs(
    limit = 3,
    filters?: { projectId?: number; runGroupId?: string }
  ): Promise<Array<{ id: number; status: string; priceCredits?: number; pipelineId?: string; pipelineTitle?: string; createdAt?: string; finishedAt?: string; thumbnailUrl?: string; thumbUrl?: string | null; batchId?: string | null; batchSize?: number | null; previewUrls?: string[]; projectId?: number | null; runGroupId?: string | null }>> {
    const params = new URLSearchParams({ limit: String(limit) })
    if (filters?.projectId != null) params.set('projectId', String(filters.projectId))
    if (filters?.runGroupId) params.set('runGroupId', filters.runGroupId)
    return this.request(`/api/jobs?${params.toString()}`)
  }

  async listJobsV1(params: { limit: number; offset?: number; status?: string; batchId?: string; projectId?: number; runGroupId?: string }): Promise<{ items: Array<{ id: number; status: string; priceCredits?: number; pipelineId?: string; pipelineTitle?: string; createdAt?: string; finishedAt?: string; thumbnailUrl?: string; thumbUrl?: string | null; batchId?: string | null; batchSize?: number | null; previewUrls?: string[]; projectId?: number | null; runGroupId?: string | null }>; total?: number }> {
    const qs = new URLSearchParams()
    qs.set('limit', String(params.limit))
    if (params.offset != null) qs.set('offset', String(params.offset))
    if (params.status) qs.set('status', params.status)
    if (params.batchId) qs.set('batchId', params.batchId)
    if (params.projectId != null) qs.set('projectId', String(params.projectId))
    if (params.runGroupId) qs.set('runGroupId', params.runGroupId)
    return this.request(`/api/jobs?${qs.toString()}`)
  }

  async getJobV1(id: number): Promise<{
    id: number
    batchId?: string | null
    pipelineId?: string
    pipelineTitle?: string
    status: 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | string
    kind?: 'GEN' | 'UPSCALE' | string
    priceCredits?: number
    progress: number
    createdAt: string
    startedAt?: string | null
    finishedAt?: string | null
    error?: string | null
    thumbUrl?: string | null
    urls: string[]
    outputs?: Array<{ id: number; url: string; rating: number | null; reasons: string[] }>
  }> {
    const legacy: any = await this.request(`/api/jobs/${id}`)
    let pipelineId: string | undefined
    let pipelineTitle: string | undefined
    const batchId = legacy?.batchId != null ? String(legacy.batchId) : null
    try {
      const local = JSON.parse(window.localStorage.getItem('jobs.local.index') || '[]') as Array<{ id: number; pipelineId?: string; title?: string }>
      const found = local.find(x => x.id === id)
      pipelineId = found?.pipelineId || legacy.pipelineId
      pipelineTitle = found?.title || legacy.pipelineTitle
    } catch { }
    return {
      id: Number(legacy.id ?? id),
      batchId,
      pipelineId,
      pipelineTitle,
      status: String(legacy.status || ''),
      kind: legacy.kind || undefined,
      priceCredits: (legacy.priceCredits != null) ? Number(legacy.priceCredits) : undefined,
      progress: Number(legacy.progress ?? 0),
      createdAt: legacy.createdAt || new Date().toISOString(),
      startedAt: legacy.startedAt ?? null,
      finishedAt: legacy.finishedAt ?? null,
      error: legacy.error ?? null,
      thumbUrl: (legacy.thumbUrl ?? legacy.thumbnailUrl ?? null),
      urls: Array.isArray(legacy.urls) ? legacy.urls : [],
      outputs: Array.isArray(legacy.outputs) ? legacy.outputs : []
    }
  }

  // News
  async listNews(limit = 5): Promise<Array<{ id: number; title: string; description?: string; linkUrl?: string; imageUrl?: string }>> {
    const qs = new URLSearchParams({ limit: String(limit) }).toString()
    return this.request(`/api/news?${qs}`)
  }

  // Projects
  async listProjects(params: {
    limit?: number
    offset?: number
    q?: string
    category?: string
    folderId?: number
    withoutFolder?: boolean
    sort?: 'updated_desc' | 'updated_asc' | 'created_desc' | 'created_asc' | 'name_asc' | 'name_desc' | string
  } = {}): Promise<ProjectsPage> {
    const qs = new URLSearchParams()
    if (params.limit != null) qs.set('limit', String(params.limit))
    if (params.offset != null) qs.set('offset', String(params.offset))
    if (params.q) qs.set('q', params.q)
    if (params.category) qs.set('category', params.category)
    if (params.folderId != null) qs.set('folderId', String(params.folderId))
    if (params.withoutFolder) qs.set('withoutFolder', 'true')
    if (params.sort) qs.set('sort', params.sort)
    const suffix = qs.toString()
    return this.request(`/api/projects${suffix ? `?${suffix}` : ''}`)
  }

  async listProjectFolders(): Promise<{
    items: ProjectFolderItem[]
    totalProjects: number
    withoutFolderCount: number
  }> {
    return this.request(`/api/projects/folders`)
  }

  async createProjectFolder(name: string): Promise<ProjectFolderItem> {
    return this.request(`/api/projects/folders`, {
      method: 'POST',
      body: JSON.stringify({ name })
    })
  }

  async renameProjectFolder(folderId: number, name: string): Promise<ProjectFolderItem> {
    return this.request(`/api/projects/folders/${folderId}`, {
      method: 'PATCH',
      body: JSON.stringify({ name })
    })
  }

  async deleteProjectFolder(folderId: number): Promise<void> {
    await this.request(`/api/projects/folders/${folderId}`, { method: 'DELETE' })
  }

  async listProjectCategories(): Promise<{ items: ProjectCategoryItem[] }> {
    return this.request(`/api/projects/categories`)
  }

  async updateProjectMeta(projectId: number, body: { category?: string | null; folderId?: number | null }): Promise<ProjectRecord> {
    return this.request(`/api/projects/${projectId}/meta`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    })
  }

  async replaceProjectTags(projectId: number, tags: string[]): Promise<ProjectRecord> {
    return this.request(`/api/projects/${projectId}/tags`, {
      method: 'PUT',
      body: JSON.stringify({ tags })
    })
  }

  async createProject(body: {
    name?: string | null
    pipelineId?: string | null
    snapshot?: Record<string, any> | null
    board?: Record<string, any> | null
  } = {}): Promise<ProjectRecord> {
    return this.request(`/api/projects`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  }

  async getProject(
    id: number,
    params: { boardGroupLimit?: number; boardGroupOffset?: number } = {}
  ): Promise<ProjectRecord> {
    const qs = new URLSearchParams()
    if (params.boardGroupLimit != null) qs.set('boardGroupLimit', String(params.boardGroupLimit))
    if (params.boardGroupOffset != null) qs.set('boardGroupOffset', String(params.boardGroupOffset))
    const suffix = qs.toString()
    return this.request(`/api/projects/${id}${suffix ? `?${suffix}` : ''}`)
  }

  async upsertProject(
    id: number,
    body: {
      pipelineId?: string | null
      snapshot?: Record<string, any> | null
      board?: Record<string, any> | null
    }
  ): Promise<ProjectRecord> {
    return this.request(`/api/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    })
  }

  async renameProject(id: number, name: string): Promise<ProjectRecord> {
    return this.request(`/api/projects/${id}/name`, {
      method: 'PATCH',
      body: JSON.stringify({ name })
    })
  }

  async deleteProject(id: number): Promise<void> {
    await this.request(`/api/projects/${id}`, { method: 'DELETE' })
  }

  async launchProjectRunGroup(
    projectId: number,
    body: {
      pipelineId: string
      pipelineVersion?: number | null
      params: Record<string, any>
      metadata?: JobMetadataDraft | null
      inputs: Array<{ assetId: number; slot: string; order?: number }>
      expectedOutputs: number
      aspect: number
      aspectLabel?: string | null
      variantParamKey?: string | null
      lineageId?: number | null
      idempotencyKey?: string | null
    }
  ): Promise<{ runGroup: ProjectRunGroupRecord; boardCards: ProjectBoardCardRecord[] }> {
    return this.request(`/api/projects/${projectId}/run-groups/launch`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  }

  async getProjectRunGroupsStatuses(
    projectId: number,
    groupIds: number[]
  ): Promise<{ runGroups: ProjectRunGroupRecord[]; boardCards: ProjectBoardCardRecord[] }> {
    return this.request(`/api/projects/${projectId}/run-groups/statuses`, {
      method: 'POST',
      body: JSON.stringify({ groupIds })
    })
  }

  async failProjectRunGroup(
    projectId: number,
    groupId: number,
    body: { errorCode?: string; errorMessage?: string } = {}
  ): Promise<void> {
    await this.request(`/api/projects/${projectId}/run-groups/${groupId}/fail`, {
      method: 'POST',
      body: JSON.stringify(body)
    })
  }

  async retryProjectBoardCard(
    projectId: number,
    cardId: number
  ): Promise<{ runGroup: ProjectRunGroupRecord; boardCards: ProjectBoardCardRecord[] }> {
    return this.request(`/api/projects/${projectId}/board-cards/${cardId}/retry`, {
      method: 'POST'
    })
  }

  async createProjectBoardCardVariant(
    projectId: number,
    cardId: number
  ): Promise<{ runGroup: ProjectRunGroupRecord; boardCards: ProjectBoardCardRecord[] }> {
    return this.request(`/api/projects/${projectId}/board-cards/${cardId}/variant`, {
      method: 'POST'
    })
  }

  // Series Generation
  async generateSeries(
    assetIds: number[],
    count: number = 4,
    extra?: {
      projectId?: number
      runGroupId?: string
      scenario_description?: string
      text_instructions?: string
      no_text_mode?: boolean
      aspect_ratio?: string
      resolution?: string
      series_style?: string
      metadata?: JobMetadataDraft
    }
  ): Promise<SeriesGenerationResponse> {
    return this.request(`/api/ai/series-plan`, {
      method: 'POST',
      body: JSON.stringify({
        assetIds,
        projectId: extra?.projectId,
        runGroupId: extra?.runGroupId,
        count,
        scenarioDescription: extra?.scenario_description,
        textInstructions: extra?.text_instructions,
        noTextMode: extra?.no_text_mode,
        aspectRatio: extra?.aspect_ratio,
        resolution: extra?.resolution,
        seriesStyle: extra?.series_style,
        metadata: extra?.metadata
      })
    })
  }

  async generateSeriesV2(
    assetIds: number[],
    count: number = 4,
    extra?: {
      projectId?: number
      runGroupId?: string
      scenario_description?: string
      text_instructions?: string
      no_text_mode?: boolean
      use_infographic_preset?: boolean
      infographic_prompt_preset?: string
      aspect_ratio?: string
      resolution?: string
      series_style?: string
      metadata?: JobMetadataDraft
    }
  ): Promise<SeriesGenerationResponse> {
    return this.request(`/api/ai/series-v2-plan`, {
      method: 'POST',
      body: JSON.stringify({
        assetIds,
        projectId: extra?.projectId,
        runGroupId: extra?.runGroupId,
        count,
        scenarioDescription: extra?.scenario_description,
        textInstructions: extra?.text_instructions,
        noTextMode: extra?.no_text_mode,
        useInfographicPreset: extra?.use_infographic_preset,
        infographicPromptPreset: extra?.infographic_prompt_preset,
        aspectRatio: extra?.aspect_ratio,
        resolution: extra?.resolution,
        seriesStyle: extra?.series_style,
        metadata: extra?.metadata
      })
    })
  }

  async generateModelingSeries(
    inputs: Array<{ assetId: number; slot: string; order?: number }>,
    count: number = 5,
    extra?: {
      projectId?: number
      runGroupId?: string
      scenario_description?: string
      outfit_description?: string
      accessories_description?: string
      locked_elements?: string
      aspect_ratio?: string
      resolution?: string
      background?: string
      lighting?: string
      effects?: boolean
      props_environment?: boolean
      preserve_look_mode?: boolean
      metadata?: JobMetadataDraft
    }
  ): Promise<SeriesGenerationResponse> {
    return this.request(`/api/ai/modeling-series-plan`, {
      method: 'POST',
      body: JSON.stringify({
        inputs,
        projectId: extra?.projectId,
        runGroupId: extra?.runGroupId,
        count,
        scenarioDescription: extra?.scenario_description,
        outfitDescription: extra?.outfit_description,
        accessoriesDescription: extra?.accessories_description,
        lockedElements: extra?.locked_elements,
        aspectRatio: extra?.aspect_ratio,
        resolution: extra?.resolution,
        background: extra?.background,
        lighting: extra?.lighting,
        effects: extra?.effects,
        propsEnvironment: extra?.props_environment,
        preserveLookMode: extra?.preserve_look_mode,
        metadata: extra?.metadata
      })
    })
  }

  async getBatchJobs(batchId: string): Promise<{ batchId: string; jobs: Array<{ id: number; pipelineId: string; status: string; priceCredits: number; createdAt: string; finishedAt?: string | null; outputs: Array<{ id: number; url?: string | null; mime?: string | null; type?: string | null }> }> }> {
    return this.request(`/api/jobs/batch/${encodeURIComponent(batchId)}`)
  }

  // Admin Partners
  async listPromoCodes(): Promise<Array<{ id: number; code: string; discountPercent: number; commissionPercent: number; usedCount: number; enabled: boolean; usageLimit?: number | null; expiresAt?: string | null }>> {
    return this.request(`/v1/admin/partners`)
  }

  async createPromoCode(data: { code: string; discountPercent: number; commissionPercent: number; usageLimit?: number; expiresAt?: string; ownerId?: number }): Promise<any> {
    return this.request(`/v1/admin/partners`, { method: 'POST', body: JSON.stringify(data) })
  }

  async syncPipelinesFromCode(mode: 'pipelines' | 'versions' | 'both', confirm = true): Promise<{
    mode: 'pipelines' | 'versions' | 'both'
    pipelinesApplied: number
    versionsCreated: number
    items: Array<{
      pipelineId: string
      existedBefore: boolean
      existedAfter: boolean
      versionBefore: number
      versionAfter: number
      versionCreated: boolean
    }>
  }> {
    return this.request(`/v1/admin/pipelines/sync-from-code`, {
      method: 'POST',
      body: JSON.stringify({ mode, confirm })
    })
  }

  // Partner Self-Service
  async listMyPromoCodes(): Promise<Array<{ id: number; code: string; discountPercent: number; commissionPercent: number; usedCount: number; enabled: boolean; usageLimit?: number | null; expiresAt?: string | null }>> {
    return this.request(`/v1/partner/codes`)
  }

  async getPartnerSummary(): Promise<{ pending: number; confirmed: number; paid: number; reverted: number; total: number }> {
    return this.request(`/v1/partner/summary`)
  }

  async listPartnerCommissions(limit = 50, offset = 0): Promise<Array<{ id: number; partnerId: number; promoCodeId: number; paymentId: number; amountCredits: number; status: string; holdUntil?: string | null; comment?: string | null; paidAt?: string | null; createdAt: string }>> {
    const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) }).toString()
    return this.request(`/v1/partner/commissions?${qs}`)
  }

  async getPartnerProfile(): Promise<{ partnerId: number; baseCommissionPercent: number; holdDays: number; activeUntil?: string | null; allowSelfReferral: boolean; minPayoutCredits: number }> {
    return this.request(`/v1/partner/profile`)
  }

  // Referral
  async getReferralSummary(): Promise<{ code: string; link: string; invitedCount: number; paidInvitedCount: number; earnedCredits: number }> {
    return this.request(`/v1/referral/me`)
  }

  async getReferralTopupStatus(): Promise<{
    referralLinked: boolean
    partnerLinked: boolean
    conflict: boolean
    invitedWelcomePercent: number
    firstTopupOnly: boolean
    firstTopupEligible: boolean
    hasPaidTopups: boolean
    showReferralNotice: boolean
    referralNoticeCode?: string | null
  }> {
    return this.request(`/v1/referral/topup-status`)
  }

  async listReferralRewards(limit = 50, offset = 0): Promise<Array<{ id: number; paymentId: number; invitedUserId: number; beneficiaryUserId: number; rewardType: string; amountCredits: number; status: string; description?: string | null; createdAt: string; revertedAt?: string | null }>> {
    const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) }).toString()
    return this.request(`/v1/referral/rewards?${qs}`)
  }

  async listReferralInvited(limit = 20, offset = 0): Promise<{
    items: Array<{ invitedUserId: number; invitedName: string; invitedAt: string; earnedCredits: number }>
    total: number
    nextOffset: number
    hasMore: boolean
  }> {
    const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) }).toString()
    return this.request(`/v1/referral/invited?${qs}`)
  }

  async bindReferral(data: { referralCode?: string; referrerId?: number }): Promise<{ attached: boolean; reason?: string | null }> {
    return this.request(`/v1/referral/bind`, { method: 'POST', body: JSON.stringify(data) })
  }

  async listNotifications(limit = 10, offset = 0): Promise<UserNotificationsPage> {
    const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) }).toString()
    return this.request(`/v1/notifications?${qs}`)
  }

  async getNotificationUnreadCount(): Promise<{ unreadCount: number }> {
    return this.request(`/v1/notifications/unread-count`)
  }

  async markNotificationRead(id: number): Promise<{ updated: number }> {
    return this.request(`/v1/notifications/${id}/read`, { method: 'PATCH' })
  }

  async markAllNotificationsRead(): Promise<{ updated: number }> {
    return this.request(`/v1/notifications/read-all`, { method: 'POST' })
  }

  async createPartnerPromoCode(data: { code: string; discountPercent: number; usageLimit?: number; expiresAt?: string }): Promise<any> {
    return this.request(`/v1/partner/codes`, { method: 'POST', body: JSON.stringify(data) })
  }

  async deletePartnerPromoCode(id: number): Promise<void> {
    await this.request(`/v1/partner/codes/${id}`, { method: 'DELETE' })
  }
}

export type SeriesSlide = {
  slide_number: number
  slide_type: 'product' | 'infographic' | 'main_card'
  scenario_title: string
  scenario_description: string
  text_instructions?: string | null
  titles?: string | null
  blocks?: string | null
  product_position?: string | null
  blocks_position?: string | null
  infographic_style?: string | null
}

export type SeriesGenerationResponse = {
  batchId: string
  slides: SeriesSlide[]
  raw?: string
}

export const api = new ApiClient()
